import { useQuery } from '@tanstack/react-query';
import { buildApiUrl } from '@/api/config';
import { apiClient } from '@/api/client';
import { useTank } from '@/contexts/TankContext';
import { CACHE_TIMES } from '@/config/cacheTimes';

interface MetricValue {
  value: number;
  timestamp: string;
  device?: string;
}

interface LatestMetrics {
  temp: MetricValue;
  ph: MetricValue;
  salinity: MetricValue;
  alkalinity: MetricValue;
  ca: MetricValue;
  mg: MetricValue;
  no3: MetricValue;
  po4: MetricValue;
}

interface UseLatestMetricsOptions {
  /** Set to false to disable the query (e.g., when data is provided from parent) */
  enabled?: boolean;
}

export function useLatestMetrics(options: UseLatestMetricsOptions = {}) {
  const { enabled = true } = options;
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery<LatestMetrics>({
    queryKey: ['latest-metrics', tankId],
    queryFn: async () => {
      const [sensorsRes, waterTestsRes] = await Promise.all([
        apiClient(buildApiUrl(`/api/tanks/${tankId}/sensors/latest`)),
        apiClient(buildApiUrl(`/api/tanks/${tankId}/water-tests/latest`)),
      ]);

      if (!sensorsRes.ok || !waterTestsRes.ok) {
        throw new Error('Failed to fetch latest metrics');
      }

      const sensors = await sensorsRes.json();
      const waterTests = await waterTestsRes.json();

      return {
        // Sensor data
        temp: sensors.temp,
        ph: sensors.ph,
        salinity: sensors.salinity,
        // Water test data
        alkalinity: waterTests.alkalinity,
        ca: waterTests.ca,
        mg: waterTests.mg,
        no3: waterTests.no3,
        po4: waterTests.po4,
      };
    },
    enabled: enabled && !!tankId,
    staleTime: CACHE_TIMES.DATA, // data only updates daily
    // Removed: refetchInterval - no need to poll, data changes once per day
  });
}
